import { Component, ViewChild, inject, input, output, effect, untracked, ChangeDetectionStrategy } from '@angular/core'
import {
  Depot,
  Supplier,
  Product,
  OrderItem,
  Customer,
  SellItem,
  Checkout,
  ProductDepot,
} from 'depoto-core/src/entities'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { ProductPriceLevelUtil } from 'depoto-core/src/utils/ProductPriceLevelUtil'
import { CoreService } from '../../../services'
import { SchemaUtil } from '../../../utils'
import { DepotService } from '../../../services/entity-services/depot.service'
import { injectQuery } from '@tanstack/angular-query-experimental'

export type StockItem = {
  depot: Depot
  productDepots: ProductDepot[]
  sumAll: number
  sumReservations: number
  sumAvailable: number
  sumPurchase: number
}
@Component({
  selector: 'modal-stock-overview',
  templateUrl: 'modal-stock-overview.component.html',
  styleUrls: ['modal-stock-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalStockOverviewComponent {
  private depotService = inject(DepotService)
  @ViewChild('modalStock') public childModal: ModalContainerComponent
  product = input<Product>()
  checkout = input<Checkout>()
  depots = input<Depot[]>([])
  suppliers = input<Supplier[]>([])
  filterDepotIdFrom = input<number>()
  filterDepots = input<number[]>([])
  // An empty filterDepots means "all depots"; this flag marks a depot scope that allows none.
  hasEmptyDepotScope = input<boolean>(false)
  filterSuppliers = input<number[]>([])
  isSmallBtn = input<boolean>(false)
  isSumCalculated = input<boolean>(false)
  isCalculatedOfProductDepots = input<boolean>(false)
  isFullWidth = input<boolean>(false)
  isSpecialAddOrderItemsInstance = input<boolean>(false)
  customer = input<Customer>()
  showPurchaseQuantity = input<boolean>(false)

  onSelectedProductDepot = output<any>() // todo type
  totalQuantityStockChange = output<any>() // todo type

  stock: any[] = []
  productPrice = 0
  sellItems: SellItem[] = []
  totalQuantityStock = 0
  totalQuantityReservation = 0
  totalQuantityPurchase = 0
  totalQuantityAvailable = 0
  isLoading = false

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => result.items)
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  constructor(protected core: CoreService) {
    effect(() => {
      this.queryDepotsOptions.data()
    })
    // Recalculate totals whenever the product or the depot scope changes; reused list rows keep this instance.
    effect(() => {
      this.product()
      this.customer()
      this.filterDepots()
      this.filterDepotIdFrom()
      this.hasEmptyDepotScope()
      untracked(() => this.calculateTotals())
    })
  }

  private calculateTotals(): void {
    this.productPrice = ProductPriceLevelUtil.getProductPriceForCustomerPriceLevel(this.product(), this.customer())

    if (this.hasEmptyDepotScope()) {
      this.setEmptyStock()
      return
    }

    this.setQuantities()

    this.totalQuantityPurchase = this.calculateQuantityPurchase(this.getEffectiveDepotIds(), this.product())
  }

  private getEffectiveDepotIds(): number[] {
    const depotIds = [...(this.filterDepots() ?? [])]

    if (this.filterDepotIdFrom() && this.filterDepotIdFrom() > 0) {
      depotIds.push(Number(this.filterDepotIdFrom()))
    }

    return [...new Set(depotIds.map(depotId => Number(depotId)).filter(depotId => depotId > 0))]
  }

  private setEmptyStock(): void {
    this.stock = []
    this.sellItems = []
    this.totalQuantityStock = 0
    this.totalQuantityReservation = 0
    this.totalQuantityAvailable = 0
    this.totalQuantityPurchase = 0
  }

  private getAvailableDepots(): Depot[] {
    return this.depots()?.length ? this.depots() : (this.queryDepotsOptions.data() as Depot[]) ?? []
  }

  private getCheckoutIdForStockQuery(): number | null {
    if (!this.core.services.user.user?.depots?.length) {
      return null
    }

    return this.checkout() ? this.checkout().id : null
  }

  areProductDepotsOnDepot(stockItem) {
    const filterDepots = this.getEffectiveDepotIds()
    return (
      (stockItem.productDepots && stockItem.productDepots.length > 0) ||
      ((!filterDepots.length || filterDepots.includes(Number(stockItem.depot?.id))) && this.showPurchaseQuantity())
    )
  }

  setQuantities() {
    const filterDepots = this.getEffectiveDepotIds()
    this.totalQuantityStock = 0
    this.totalQuantityReservation = 0
    this.totalQuantityAvailable = 0
    if (this.product()?.quantities?.length) {
      const quantities =
        filterDepots?.length > 0
          ? this.product()?.quantities.filter(q => filterDepots.some(fD => Number(fD) === Number(q.depot.id)))
          : this.product()?.quantities
      quantities.forEach(quantity => {
        // todo tohle byl pornlem pri aktivnim???
        if (
          !filterDepots?.length ||
          (filterDepots?.length > 0 && filterDepots.some(d => Number(d) === Number(quantity.depot.id)))
        ) {
          this.totalQuantityStock = Number(this.totalQuantityStock) + Number(quantity.quantityStock)
          this.totalQuantityReservation = Number(this.totalQuantityReservation) + Number(quantity.quantityReservation)
          this.totalQuantityAvailable = Number(this.totalQuantityAvailable) + Number(quantity.quantityAvailable)
        }
      })
    } else {
      this.totalQuantityAvailable = this.product().quantityAvailable
      this.totalQuantityReservation = this.product().quantityReservation
      this.totalQuantityStock = this.product().quantityStock
      this.totalQuantityPurchase = this.product().quantityPurchase
    }

    if (this.totalQuantityAvailable < 0) {
      this.totalQuantityAvailable = 0
    }
  }

  private calculateQuantityPurchase(depots: Array<number>, product: Product): number {
    let result = 0
    if (depots?.length > 0 && product?.quantities?.length) {
      const selectedQuantitiesAccordingToDepots = product.quantities.filter(item =>
        depots.includes(Number(item.depot.id))
      )

      result = selectedQuantitiesAccordingToDepots.reduce(
        (accumulator, currentValue) => accumulator + currentValue.quantityPurchase,
        0
      )
    } else {
      result = this.product().quantityPurchase || 0
    }
    return result
  }

  private filterSellItemsByDepots(sellItems: SellItem[], filterDepots: number[]): SellItem[] {
    if (!filterDepots.length) {
      return sellItems
    }

    return sellItems
      .map(sellItem => {
        const productDepots = sellItem.productDepots.filter(pDepot => filterDepots.includes(Number(pDepot.depot?.id)))
        if (!productDepots.length) {
          return null
        }

        return new SellItem({
          ...sellItem,
          productDepots,
          quantityStock: productDepots.reduce((total, pDepot) => total + Number(pDepot.quantityStock || 0), 0),
          quantityReservation: productDepots.reduce(
            (total, pDepot) => total + Number(pDepot.quantityReservation || 0),
            0
          ),
          quantityAvailable: productDepots.reduce((total, pDepot) => total + Number(pDepot.quantityAvailable || 0), 0),
        })
      })
      .filter((sellItem): sellItem is SellItem => !!sellItem)
  }

  private hasProductStockQuantities(): boolean {
    const product = this.product()

    return !!(
      product?.quantityStock ||
      product?.quantityReservation ||
      product?.quantityAvailable ||
      product?.quantities?.some(
        quantity => quantity.quantityStock || quantity.quantityReservation || quantity.quantityAvailable
      )
    )
  }

  private hasSellItemStockQuantities(): boolean {
    return this.sellItems.some(sellItem => {
      return sellItem.quantityStock || sellItem.quantityReservation || sellItem.quantityAvailable
    })
  }

  private setStockFromProductQuantities(): void {
    const filterDepots = this.getEffectiveDepotIds()
    const availableDepots = this.getAvailableDepots()
    const quantities = this.product()?.quantities ?? []
    const selectedQuantities = filterDepots.length
      ? quantities.filter(quantity => filterDepots.includes(Number(quantity.depot?.id)))
      : quantities

    this.stock = selectedQuantities.map(quantity => {
      const depotId = Number(quantity.depot?.id)
      return {
        depot: availableDepots.find(depot => Number(depot.id) === depotId) ?? quantity.depot,
        productDepots: [],
        sumAll: quantity.quantityStock || 0,
        sumReservations: quantity.quantityReservation || 0,
        sumAvailable: quantity.quantityAvailable || 0,
        sumPurchase: quantity.quantityPurchase || 0,
        isUsersDepot: availableDepots.some(depot => Number(depot.id) === depotId),
      }
    })
  }

  private setQuantitiesFromSellItems(): void {
    if (!this.sellItems.length || (!this.hasSellItemStockQuantities() && this.hasProductStockQuantities())) {
      this.sellItems = []
      this.setStockFromProductQuantities()
      this.setQuantities()
      return
    }

    this.totalQuantityAvailable = Object.values(this.sellItems).reduce(
      (total, { quantityAvailable }) => total + quantityAvailable,
      0
    )
    this.totalQuantityReservation = Object.values(this.sellItems).reduce(
      (total, { quantityReservation }) => total + quantityReservation,
      0
    )
    this.totalQuantityStock = Object.values(this.sellItems).reduce(
      (total, { quantityStock }) => total + quantityStock,
      0
    )
  }

  getQuantities(): void {
    if (this.hasEmptyDepotScope()) {
      this.setEmptyStock()
      this.totalQuantityStockChange.emit(this.totalQuantityStock)
      return
    }

    if (this.product()?.isBundle) {
      this.setQuantitiesForBundle()
    } else {
      this.isCalculatedOfProductDepots() ? this.formatStockDetail() : this.getSellItems()
    }
  }

  setQuantitiesForBundle() {
    const filterDepots = this.getEffectiveDepotIds()
    const stock: any[] = []
    const availableDepots = this.getAvailableDepots()
    const depotsForLoop = filterDepots.length ? filterDepots : availableDepots.map(d => d.id)
    // const depotsForLoop = this.filterDepots?.length ? this.filterDepots : this.core.lists.depots.map(d => d.id)
    depotsForLoop.forEach(depot => {
      const quantity = this.product().quantities?.find(q => Number(q.depot.id) === Number(depot))
      stock.push({
        depot: availableDepots.find(d => Number(d.id) === Number(depot)),
        productDepots: [],
        sumAll: quantity?.quantityStock || 0,
        sumReservations: quantity?.quantityReservation || 0,
        sumAvailable: quantity?.quantityAvailable || 0,
        sumPurchase: quantity?.quantityPurchase || 0,
        isUsersDepot: availableDepots.some(d => Number(d.id) === Number(depot)),
      })
    })
    this.stock = stock
  }

  private async getSellItems() {
    this.isLoading = true
    const filterDepots = this.getEffectiveDepotIds()
    // TODO CORE QUERY
    this.sellItems = await this.core.services.product
      .getProductSellItems(this.product().id, this.getCheckoutIdForStockQuery(), filterDepots, SchemaUtil.sellItem)
      .then(result => result.items.map(sellItem => new SellItem(sellItem)))
    this.sellItems = this.filterSellItemsByDepots(this.sellItems, filterDepots)
    this.setQuantitiesFromSellItems()
    this.totalQuantityPurchase = this.calculateQuantityPurchase(filterDepots, this.product())
    this.totalQuantityStockChange.emit(this.totalQuantityStock)
    this.isLoading = false
  }

  public showChildModal(): void {
    this.getQuantities()
    this.childModal.showChildModal()
  }

  public hideChildModal(): void {
    this.childModal.hideChildModal()
  }

  public addOrderItem(sellItem: SellItem) {
    const orderItem = new OrderItem({ ...this.product(), ...{ id: null } })
    orderItem.product = this.product()
    orderItem.quantity = sellItem.orderQuantity
    orderItem.price = this.productPrice
    orderItem.priceAll = this.productPrice * sellItem.orderQuantity
    orderItem.expirationDate = sellItem.expirationDate
    orderItem.batch = sellItem.batch
    orderItem.name = this.product().fullName
    orderItem.position = sellItem.productDepots[0].position
    this.onSelectedProductDepot.emit(orderItem)
  }

  areJustNullQuantitiesInTable(sellItemOrItems: SellItem[] | ProductDepot[]): boolean {
    return sellItemOrItems.every(
      item =>
        (!item.quantityStock || item.quantityStock <= 0) && (!item.quantityReservation || item.quantityReservation <= 0)
    )
  }

  async formatStockDetail(): Promise<void> {
    if (!this.product().productDepots?.length) {
      this.isLoading = true
      // TODO CORE QUERY
      const r = await this.core.services.product.getById(this.product().id, {
        id: null,
        productDepots: {
          id: null,
          depot: { id: null, name: null, unavailablesUrl: null },
          supplier: { id: null, name: null },
          purchasePrice: null,
          quantityStock: null,
          quantityReservation: null,
          quantityAvailable: null,
          inventoryQuantityStock: null,
          batch: null,
          expirationDate: null,
          position: null,
          position1: null,
          position2: null,
          position3: null,
          created: null,
          updated: null,
        },
      })
      if (r?.productDepots) {
        if (this.product().productDepots) {
          this.product().productDepots = [...r.productDepots]
        } else {
          this.product.apply(new Product({ ...this.product(), ...r.productDepots }))
        }
      }
    }
    const filterDepots = this.getEffectiveDepotIds()
    const filterSuppliers: number[] = [...this.filterSuppliers()]
    const stock: any[] = []
    const availableDepots = this.getAvailableDepots()
    this.totalQuantityStock = 0
    this.totalQuantityReservation = 0
    this.totalQuantityAvailable = 0
    this.totalQuantityPurchase = 0
    if (availableDepots.length || filterDepots.length) {
      const depotsForLoop = filterDepots.length ? filterDepots : availableDepots.map(d => d.id)
      depotsForLoop.forEach(depot => {
        const quantity = this.product().quantities?.find(q => {
          return Number(q.depot.id) === Number(depot)
        })
        if (quantity) {
          stock.push({
            depot: availableDepots.find(d => Number(d.id) === Number(depot)),
            productDepots: [],
            sumAll: quantity.quantityStock || 0,
            sumReservations: quantity.quantityReservation || 0,
            sumAvailable: quantity.quantityAvailable || 0,
            sumPurchase: quantity.quantityPurchase || 0,
            isUsersDepot: availableDepots.some(d => Number(d.id) === Number(depot)),
          })
        }
      })
    }
    if (stock.length) {
      stock.forEach(stockItem => {
        this.totalQuantityStock = Number(this.totalQuantityStock) + Number(stockItem.sumAll)
        this.totalQuantityReservation = Number(this.totalQuantityReservation) + Number(stockItem.sumReservations)
        this.totalQuantityAvailable = Number(this.totalQuantityAvailable) + Number(stockItem.sumAvailable)
        this.totalQuantityPurchase = Number(this.totalQuantityPurchase) + Number(stockItem.sumPurchase)
      })
    }
    if (this.product()?.productDepots?.length) {
      this.totalQuantityStock = 0
      this.totalQuantityReservation = 0
      this.totalQuantityAvailable = 0
      this.product().productDepots.forEach(pDepot => {
        if (
          (!filterDepots.length || filterDepots.includes(Number(pDepot.depot?.id))) &&
          (!filterSuppliers.length || filterSuppliers.includes(Number(pDepot.supplier?.id)))
        ) {
          this.totalQuantityStock = Number(this.totalQuantityStock) + Number(pDepot.quantityStock)
          this.totalQuantityReservation = Number(this.totalQuantityReservation) + Number(pDepot.quantityReservation)
          this.totalQuantityAvailable = Number(this.totalQuantityAvailable) + Number(pDepot.quantityAvailable)
          stock.forEach(stockItem => {
            if (Number(pDepot.depot.id) === Number(stockItem.depot?.id)) {
              stockItem.productDepots.push(pDepot)
            }
          })
        }
      })
    } else if (!stock.length) {
      this.totalQuantityAvailable = this.product().quantityAvailable
      this.totalQuantityReservation = this.product().quantityReservation
      this.totalQuantityStock = this.product().quantityStock
      this.totalQuantityPurchase = this.product().quantityPurchase
    } else if (!this.totalQuantityPurchase) {
      this.totalQuantityPurchase = this.calculateQuantityPurchase(filterDepots, this.product())
    }
    this.totalQuantityStockChange.emit(this.totalQuantityStock)

    if (this.totalQuantityAvailable < 0) {
      this.totalQuantityAvailable = 0
    }
    this.stock = stock
    this.isLoading = false
  }
}
